import React, { useMemo, useState } from 'react';
import { grammarUnits } from '../data/grammarBooks/index.js';
import GrammarUnitCard from './GrammarUnitCard';
import GrammarUnitContent from './GrammarUnitContent';
import { useSupabaseData } from '../hooks/useSupabaseData';


const GrammarBookPage = ({ onBack, userPreferences }) => {
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [userProgress, setUserProgress] = useState({});

  // Load units from Supabase with fallback to local static data
  const { data: remoteUnits } = useSupabaseData({
    table: 'eng_grammarbooks',
    mapRow: (row) => ({
      id: row.unit_id || row.id,
      title: {
        traditional: row.title_traditional || row.title_tc || row.title,
        simplified: row.title_simplified || row.title_sc || row.title,
        english: row.title_english || row.title_en || row.title
      },
      subtitle: {
        traditional: row.subtitle_traditional || row.subtitle_tc || row.subtitle,
        simplified: row.subtitle_simplified || row.subtitle_sc || row.subtitle,
        english: row.subtitle_english || row.subtitle_en || row.subtitle
      },
      description: {
        traditional: row.description_traditional || row.description_tc || row.description,
        simplified: row.description_simplified || row.description_sc || row.description,
        english: row.description_english || row.description_en || row.description
      },
      status: row.status || 'notStarted',
      difficulty: row.difficulty || (row.id <= 6 ? 'beginner' : row.id <= 12 ? 'intermediate' : 'advanced')
    }),
    getFallback: () => grammarUnits
  });

  const units = useMemo(() => {
    const list = Array.isArray(remoteUnits) ? remoteUnits : [];
    // Ensure predictable ordering regardless of column naming in DB
    return [...list].sort((a, b) => Number(a.id) - Number(b.id));
  }, [remoteUnits]);

  

  const getText = (textObj) => {
    return textObj[userPreferences.displayLanguage] || textObj.traditional;
  };

  const texts = {
    title: { traditional: '語法書', simplified: '语法书', english: 'Grammar Book' },
    subtitle: { traditional: '系統性語法學習', simplified: '系统性语法学习', english: 'Systematic Grammar Learning' },
    description: { traditional: '完整的語法學習課程，包含基礎到進階的所有語法結構。', simplified: '完整的语法学习课程，包含基础到进阶的所有语法结构。', english: 'Complete grammar learning course covering all grammar structures from basic to advanced.' },
    back: { traditional: '返回', simplified: '返回', english: 'Back' },
    unit: { traditional: '單元', simplified: '单元', english: 'UNIT' },
    lessons: { traditional: '課程', simplified: '课程', english: 'Lessons' },
    completed: { traditional: '已完成', simplified: '已完成', english: 'Completed' },
    inProgress: { traditional: '進行中', simplified: '进行中', english: 'In Progress' },
    notStarted: { traditional: '未開始', simplified: '未开始', english: 'Not Started' },
    beginner: { traditional: '初級', simplified: '初级', english: 'BEGINNER' },
    intermediate: { traditional: '中級', simplified: '中级', english: 'INTERMEDIATE' },
    advanced: { traditional: '高級', simplified: '高级', english: 'ADVANCED' }
  };

  const markUnitComplete = (unitId) => {
    setUserProgress(prev => ({
      ...prev,
      [unitId]: true
    }));
  };

  const handleUnitClick = (unitId) => {
    setSelectedUnit(unitId);
  };

  const handleBackToUnits = () => {
    setSelectedUnit(null);
  };

  const getDifficultyLevel = (unitId) => {
    if (unitId <= 6) return getText(texts.beginner);
    if (unitId <= 12) return getText(texts.intermediate);
    return getText(texts.advanced);
  };

  const getUnitStatus = (unit) => {
    if (userProgress[unit.id]) return 'completed';
    if (unit.status === 'completed') return 'completed';
    if (unit.status === 'inProgress') return 'inProgress';
    return 'notStarted';
  };

  if (selectedUnit) {
    const unit = units.find(u => u.id === selectedUnit);
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/20">
          <button 
            onClick={handleBackToUnits} 
            className="text-blue-400 hover:text-blue-300 transition-colors"
          >
            ← {getText(texts.back)}
          </button>
          <h1 className="text-xl font-bold">{getText(unit.title)}</h1>
          <div className="w-8"></div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
            <h2 className="text-2xl font-bold mb-2">{getText(unit.subtitle)}</h2>
            <p className="text-gray-300 mb-4">{getText(unit.description)}</p>
            
            <div className="flex items-center gap-4 mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                unit.status === 'completed' ? 'bg-green-500' : 
                unit.status === 'inProgress' ? 'bg-yellow-500' : 'bg-gray-400'
              }`}>
                {unit.status === 'completed' ? getText(texts.completed) :
                 unit.status === 'inProgress' ? getText(texts.inProgress) : 
                 getText(texts.notStarted)}
              </span>
              {unit.status === 'completed' && (
                <button
                  onClick={() => markUnitComplete(unit.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  ✓ {getText(texts.completed)}
                </button>
              )}
            </div>
          </div>

          <div className="bg-white text-gray-800 rounded-xl overflow-hidden">
            <GrammarUnitContent 
              unit={unit}
              userPreferences={userPreferences}
              onMarkComplete={markUnitComplete}
              userProgress={userProgress}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-6">
        <button onClick={onBack} className="text-blue-400 hover:text-blue-300 transition-colors">
          ← {getText(texts.back)}
        </button>
        <h1 className="text-xl font-bold">{getText(texts.title)}</h1>
        <div className="w-8"></div>
      </div>

      {/* Content */}
      <div className="px-6">
        {/* Unit Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">{getText(texts.unit)} 1</h2>
          <p className="text-gray-300 mb-4">1/{units.length} {getText(texts.lessons)}</p>
          <h3 className="text-2xl font-bold mb-2">{units[0] ? getText(units[0].title) : ''}</h3>
          <p className="text-gray-300 text-sm leading-relaxed max-w-md mx-auto">
            {units[0] ? getText(units[0].description) : ''}
          </p>
        </div>

        {/* Lesson Cards */}
        <div className="space-y-4">
          {units.map((unit) => {
            const status = getUnitStatus(unit);
            const isCompleted = status === 'completed';
            const isInProgress = status === 'inProgress';
            
            return (
              <div 
                key={unit.id}
                onClick={() => handleUnitClick(unit.id)}
                className="bg-gray-800/50 rounded-xl p-6 cursor-pointer hover:bg-gray-800/70 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Left: Number Badge */}
                  <div className="bg-blue-500 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                    {unit.id}
                  </div>

                  {/* Center: Content */}
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-white mb-2">{getText(unit.title)}</h4>
                    <p className="text-gray-300 text-sm mb-3 leading-relaxed">{getText(unit.description)}</p>
                    
                    {/* Progress Bar for completed/in-progress units */}
                    {(isCompleted || isInProgress) && (
                      <div className="mb-3">
                        <div className="w-full bg-gray-600 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              isCompleted ? 'bg-green-500' : 'bg-yellow-500'
                            }`}
                            style={{ width: isCompleted ? '100%' : '60%' }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-xs">1 {getText(texts.lessons).toLowerCase()}</span>
                    </div>
                  </div>

                  {/* Right: Difficulty and Status */}
                  <div className="flex flex-col items-end gap-3">
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                      {getDifficultyLevel(unit.id)}
                    </span>
                    
                    {/* Status Indicator */}
                    <div className={`w-4 h-4 rounded-full ${
                      isCompleted ? 'bg-green-500' : 
                      isInProgress ? 'bg-yellow-500' : 
                      'border-2 border-gray-400'
                    }`}></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress Summary */}
        <div className="mt-8 bg-gray-800/30 rounded-xl p-6">
          <h3 className="text-lg font-bold mb-4">學習進度</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-green-500/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-400">
                {units.filter(u => getUnitStatus(u) === 'completed').length}
              </div>
              <div className="text-sm text-gray-300">{getText(texts.completed)}</div>
            </div>
            <div className="bg-yellow-500/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-400">
                {units.filter(u => getUnitStatus(u) === 'inProgress').length}
              </div>
              <div className="text-sm text-gray-300">{getText(texts.inProgress)}</div>
            </div>
            <div className="bg-gray-500/20 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-400">
                {units.filter(u => getUnitStatus(u) === 'notStarted').length}
              </div>
              <div className="text-sm text-gray-300">{getText(texts.notStarted)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrammarBookPage;
